import { useEffect, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import CompanyHeader from '@/components/company/CompanyHeader';
import FormBuilder, { FormField } from '@/components/company/FormBuilder';
import useCompanyState from '@/states/company.state';
import useTenantState from '@/states/tenant.state';
import { useParams } from 'react-router-dom';
import useCompanyProfileCompletionState from '@/states/completion.state';
import useOrgChartState from '@/states/organization.state';

interface CompanyFormValues {
  information: string;
  file_upload: any;
  is_published_information: boolean;
}

export default function CompanyInformation() {
  const formControl = useForm<CompanyFormValues>({
    defaultValues: {
      information: '',
      file_upload: '',
      is_published_information: false,
    },
  });

  const { handleSubmit, reset, watch, setValue } = formControl;
  const formRef = useRef<HTMLFormElement>(null);
  const BASE_URL = import.meta.env.VITE_MINIO_BASE_URL;
  const { fetchCompletion } = useCompanyProfileCompletionState();

  const normalizeFile = (file: any) => {
    if (!file) return null;

    const path = file.url?.replace(/^https?:\/\/[^/]+/, '') || file.url;

    return {
      ...file,
      name: file.filename || file.name,
      original_url: BASE_URL + path,
    };
  };

  const {
    companyProfile,
    getCompanyInformation,
    createCompanyInformation,
    updateCompanyInfo,
    uploadCompanyMedia,
    isLoading,
    clearCompanyProfile,
  } = useCompanyState();
  const { currentTenant } = useTenantState();

  useEffect(() => {
    if (!currentTenant?.id) return;

    clearCompanyProfile();
    reset({
      information: '',
      file_upload: '',
      is_published_information: false,
    });

    getCompanyInformation(currentTenant.id);
  }, [currentTenant?.id]);

  useEffect(() => {
    if (!companyProfile) return;

    const informationValue =
      typeof companyProfile.information === 'object' ? companyProfile.information?.value : companyProfile.information;

    const publishedValue =
      typeof companyProfile.information === 'object'
        ? companyProfile.information?.is_published
        : companyProfile.is_published_information;

    const parsedFiles = (companyProfile as any)?.parsedFiles || [];

    const normalizedFiles = parsedFiles.map(normalizeFile);

    reset({
      information: informationValue ?? '',
      file_upload: normalizedFiles.length > 0 ? normalizedFiles[0] : '',
      is_published_information: !!publishedValue,
    });
  }, [companyProfile, reset]);

  const handleSave = async (data: CompanyFormValues) => {
    if (!currentTenant?.id) return;

    try {
      const uploadedFile = data.file_upload instanceof File ? data.file_upload : null;
      let uploadedUrl = null;

      if (uploadedFile) {
        const uploadResponse = await uploadCompanyMedia(uploadedFile, currentTenant.id, uploadedFile.name);
        uploadedUrl = uploadResponse?.data?.url || uploadResponse?.url;
      }

      const payload: any = {
        information: data.information,
        is_published_information: data.is_published_information,
      };

      const existingFiles = (companyProfile as any)?.parsedFiles || [];

      if (!uploadedFile && !data.file_upload) {
        payload.knowledge_base = JSON.stringify([]);
      } else if (uploadedFile && uploadedUrl) {
        const newFile = {
          filename: uploadedFile.name,
          mime_type: uploadedFile.type,
          size: uploadedFile.size,
          extension: uploadedFile.name.split('.').pop(),
          url: uploadedUrl,
          uploaded_at: new Date().toISOString(),
        };
        payload.knowledge_base = JSON.stringify([newFile]);
      } else {
        payload.knowledge_base = JSON.stringify(existingFiles);
      }

      const hasExistingData = !!companyProfile?.id;

      if (hasExistingData) {
        await updateCompanyInfo(currentTenant.id, payload);
      } else {
        await createCompanyInformation(currentTenant.id, payload);
      }

      await getCompanyInformation(currentTenant.id);
    } catch (error) {
      console.error(error);
    }
  };

  const fields: FormField[] = [
    {
      name: 'information',
      label: 'Company Information',
      type: 'markdown',
      required: true,
      placeholder: "Describe your company's core profile or background...",
    },
    {
      name: 'file_upload',
      label: 'File Upload',
      type: 'upload',
      uploadType: 'file',
      onFileChange: (file: File | null) => setValue('file_upload', file),
    },
  ];

  const isPublished = watch('is_published_information');

  return (
    <section className="w-full">
      <CompanyHeader
        title="Company Information"
        description="Update your company's profile information and attach relevant documents."
        onSave={() => formRef.current?.requestSubmit()}
        isSaving={isLoading}
      />

      <div className="mx-5 border-b border-gray-200" />

      <FormProvider {...formControl}>
        <form ref={formRef} onSubmit={handleSubmit(handleSave)} className="pt-5 space-y-4">
          <FormBuilder
            fields={fields}
            initialPublished={isPublished}
            onPublishChange={v => setValue('is_published_information', v)}
          />
        </form>
      </FormProvider>
    </section>
  );
}
